import React, { useEffect, useMemo } from 'react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useOnboardingInput } from '../../hooks/useOnboardingInput';
import { Xmark } from 'iconoir-react';
import { MdAttachFile, MdEast } from 'react-icons/md';
import { PiX } from 'react-icons/pi';

interface EditFooterProps {
  className?: string;
  questionIndex: number;
  questionId: string;
  uploaderPreviewClassName?: string;
  aiText: string;
  hasNestedQuestion: boolean;
  onUpdateCallback?: () => void;
}

const EditFooter = ({
  className,
  questionIndex,
  questionId,
  uploaderPreviewClassName = '',
  aiText,
  hasNestedQuestion,
  onUpdateCallback,
}: EditFooterProps) => {
  const {
    questions,
    answer,
    isAnswerUpdating,
    isLoading: _isLoading,
    isQuestionLoading: _isQuestionLoading,
    setAnswer,
    setSelected,
    updateAnswer,
    setIsAnswerUpdating,
  } = useOnboarding();

  const currentQuestion = useMemo(
    () => questions.find(question => question.id === questionId),
    [questions, questionId]
  );

  const {
    textareaRef,
    fileInputRef,
    files,
    errors,
    clearFiles,
    handleFileChange,
    openFileDialog,
    handleChange,
    validateAnswer,
    processResumeLinkedInAnswer,
    parseExistingAnswer,
    isResumeLinkedInQuestion,
    MIN_ROWS,
  } = useOnboardingInput({
    answer,
    setAnswer,
    isAnswerUpdating,
    currentQuestionCode: currentQuestion?.code,
  });

  const handleCancelAnswer = () => {
    setSelected('');
    setAnswer('');
    clearFiles();
  };

  const handleUpdatedAnswerSubmission = async (
    questionId: string,
    answer: string,
    aiText: string
  ) => {
    if (isAnswerUpdating) return;

    try {
      setIsAnswerUpdating(true);

      if (!validateAnswer(files.length)) {
        setIsAnswerUpdating(false);
        return;
      }

      // Handle resume and linkedin bio questions
      if (isResumeLinkedInQuestion) {
        const userResponse = await processResumeLinkedInAnswer();
        if (!userResponse) {
          setIsAnswerUpdating(false);
          return;
        }

        updateAnswer(
          questionId,
          JSON.stringify(userResponse),
          aiText,
          hasNestedQuestion,
          questionIndex,
          onUpdateCallback
        );
      } else {
        updateAnswer(
          questionId,
          answer,
          aiText,
          hasNestedQuestion,
          questionIndex,
          onUpdateCallback
        );
      }

      clearFiles();
    } catch (error) {
      console.error(error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleUpdatedAnswerSubmission(questionId, answer, aiText);
    }
  };

  // Parse existing answer when editing resume/linkedin question
  useEffect(() => {
    if (isResumeLinkedInQuestion && answer) {
      parseExistingAnswer(answer);
    }
  }, [currentQuestion]);

  return (
    <div className='mt-12'>
      {errors.length > 0 && (
        <div className='mb-3'>
          {errors.map((error, index) => (
            <p key={index} className='text-sm text-red-500 mb-1'>
              {error}
            </p>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div
          className={`flex items-center justify-between gap-3 px-3 py-[0.875rem] border-2 border-gray-200 rounded-xl w-fit mb-3 ${uploaderPreviewClassName}`}
        >
          <span
            className={`font-heading text-lg font-medium ${files[0].name.split('.').pop() === 'pdf' ? 'text-red-500' : 'text-blue-500'}`}
          >
            {files[0].name.split('.').pop()?.toUpperCase()}
          </span>
          <span className='px-[0.8125rem] text-sm font-bold'>
            {files[0].name}
          </span>
          <PiX
            size={24}
            className='cursor-pointer text-red-500'
            onClick={clearFiles}
          />
        </div>
      )}
      <div
        className={`w-full mb-16 flex items-center justify-between gap-3 min-h-14 border-gray-200 border-2 rounded-lg px-3 py-2 w-full [&:focus-within]:border-primary-200 duration-200 ${className}`}
      >
        <textarea
          ref={textareaRef}
          className='w-full resize-none focus:outline-none'
          rows={MIN_ROWS}
          value={answer}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isAnswerUpdating}
          placeholder='Enter your answer here'
        />
        <div className='flex self-end gap-2 mb-[2px]'>
          {isResumeLinkedInQuestion && (
            <button
              className='bg-primary-400 text-white rounded-md flex items-center justify-center w-8 h-8 focus:outline-none focus:ring-0 hover:bg-primary-500 duration-200 cursor-pointer'
              onClick={openFileDialog}
              disabled={isAnswerUpdating}
            >
              <input
                multiple={false}
                type='file'
                className='hidden'
                ref={fileInputRef}
                onChange={handleFileChange}
                accept='.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                disabled={isAnswerUpdating}
              />
              <MdAttachFile size={20} />
            </button>
          )}
          <button
            className='text-red-500 hover:text-red-700 flex items-center justify-center w-8 h-8 focus:outline-none focus:ring-0 cursor-pointer duration-200'
            onClick={handleCancelAnswer}
            disabled={isAnswerUpdating}
          >
            <Xmark className='w-8 h-8' />
          </button>
          <button
            className='text-primary-400 flex items-center justify-center w-8 h-8 focus:outline-none focus:ring-0 cursor-pointer disabled:text-gray-400 disabled:hover:text-gray-400'
            disabled={(answer.length === 0 && files.length === 0) || isAnswerUpdating}
            onClick={() =>
              handleUpdatedAnswerSubmission(questionId, answer, aiText)
            }
          >
            <MdEast size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditFooter;
